import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SettingsIcon, GitMergeIcon, WorkflowIcon, DatabaseIcon, MessageSquareIcon, FileTextIcon, CheckCircleIcon, ArrowRightIcon, SearchIcon, MapIcon, WrenchIcon, ActivityIcon, RocketIcon, LayersIcon, CpuIcon, AlertCircleIcon } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal } from '../components/ScrollReveal';
export function CustomAutomationsPage() {
  return <div className="pt-24 pb-16" data-id="element-1033">
      <SEOHead title="Custom Automations & API Integrations | Viridia Analytics" description="Bespoke automation workflows for local businesses. Connect your tools, eliminate manual data entry, and build systems tailored to your unique operations." canonical="/custom-automations" keywords="custom automations, API integrations, workflow automation, business process automation, Zapier alternative, custom webhooks, Phoenix automation agency" data-id="element-1034" />

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 overflow-hidden" data-id="element-1035">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-viridia/10 via-viridia-black to-viridia-black -z-10" data-id="element-1036"></div>

        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgNDBoNDBWMEgwem0zOS0zOXYzOEgxVjFoMzh6IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDIpIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] -z-10 opacity-50" data-id="element-1037"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1038">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1039">
            <ScrollReveal data-id="element-1040">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-medium mb-6" data-id="element-1041">
                <SettingsIcon className="w-4 h-4" data-id="element-1042" />
                <span data-id="element-1043">Bespoke Systems Engineering</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-id="element-1044">
                Connect Your Tools. <br data-id="element-1045" />
                <span className="text-viridia" data-id="element-1046">Automate the Rest.</span>
              </h1>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl" data-id="element-1047">
                When off-the-shelf software doesn't fit your unique process, we
                build custom workflows that make your existing tools talk to
                each other. Eliminate manual data entry and operational
                bottlenecks.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1048">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1049">
                  Book a Scoping Call
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1050">
              <div className="relative" data-id="element-1051">
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative overflow-hidden" data-id="element-1052">
                  {/* Workflow Diagram Visualization */}
                  <div className="flex flex-col items-center gap-4 relative z-10 py-4" data-id="element-1053">
                    {/* Trigger */}
                    <motion.div initial={{
                    opacity: 0,
                    y: -20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.5
                  }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg" data-id="element-1054">
                      <div className="w-8 h-8 rounded bg-blue-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1055">
                        <FileTextIcon className="w-4 h-4 text-blue-400" data-id="element-1056" />
                      </div>
                      <div className="min-w-0" data-id="element-1057">
                        <div className="text-xs text-gray-500" data-id="element-1058">Trigger</div>
                        <div className="text-sm font-medium text-white truncate" data-id="element-1059">
                          New Quote Form Submitted
                        </div>
                      </div>
                    </motion.div>

                    <div className="w-px h-8 bg-gray-700" data-id="element-1060"></div>

                    {/* Logic Branch */}
                    <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.5,
                    delay: 0.3
                  }} className="bg-viridia-black border border-viridia/30 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg relative" data-id="element-1061">
                      <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1062"></div>
                      <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1063"></div>

                      <div className="w-8 h-8 rounded bg-viridia/20 flex items-center justify-center" data-id="element-1064">
                        <CpuIcon className="w-4 h-4 text-viridia" data-id="element-1065" />
                      </div>
                      <div data-id="element-1066">
                        <div className="text-xs text-gray-500" data-id="element-1067">Action</div>
                        <div className="text-sm font-medium text-white" data-id="element-1068">
                          AI Summarizes Request
                        </div>
                      </div>
                    </motion.div>

                    <div className="flex w-full max-w-md justify-between px-12 relative" data-id="element-1069">
                      <div className="w-px h-8 bg-gray-700 absolute left-1/4 top-0" data-id="element-1070"></div>
                      <div className="w-px h-8 bg-gray-700 absolute right-1/4 top-0" data-id="element-1071"></div>
                      <div className="w-1/2 h-px bg-gray-700 absolute left-1/4 top-0" data-id="element-1072"></div>
                    </div>

                    {/* Outcomes */}
                    <div className="flex gap-4 sm:gap-8 w-full justify-center flex-wrap sm:flex-nowrap" data-id="element-1073">
                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.6
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1074">
                        <div className="w-8 h-8 rounded bg-green-500/20 flex items-center justify-center" data-id="element-1075">
                          <DatabaseIcon className="w-4 h-4 text-green-400" data-id="element-1076" />
                        </div>
                        <div data-id="element-1077">
                          <div className="text-xs text-gray-500" data-id="element-1078">CRM</div>
                          <div className="text-sm font-medium text-white" data-id="element-1079">
                            Create Record
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      x: 20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.8
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1080">
                        <div className="w-8 h-8 rounded bg-purple-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1081">
                          <MessageSquareIcon className="w-4 h-4 text-purple-400" data-id="element-1082" />
                        </div>
                        <div className="min-w-0" data-id="element-1083">
                          <div className="text-xs text-gray-500" data-id="element-1084">Slack</div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-1085">
                            Alert Sales Team
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Animated Pulses */}
                    <motion.div animate={{
                    top: ['10%', '90%'],
                    opacity: [0, 1, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-viridia rounded-full shadow-[0_0_10px_rgba(16,185,129,0.8)] z-20" data-id="element-1086"></motion.div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* When Custom Makes Sense */}
      <section className="py-20 bg-viridia-black" data-id="element-1087">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1088">
          <ScrollReveal data-id="element-1089">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1090">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1091">
                When Do You Need Custom Automations?
              </h2>
              <p className="text-gray-400" data-id="element-1092">
                Our standard packages cover 90% of local business needs. Custom
                automations are for the 10% that makes your business unique.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1093">
            <ScrollReveal data-id="element-1094">
              <div className="space-y-6" data-id="element-1095">
                <div className="flex items-start gap-4" data-id="element-1096">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1097">
                    <LayersIcon className="w-5 h-5 text-viridia" data-id="element-1098" />
                  </div>
                  <div data-id="element-1099">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1100">
                      Disconnected Software Stack
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1101">
                      Your field service software (like ServiceTitan or Jobber)
                      doesn't talk to your CRM or accounting software, forcing
                      you to enter data twice.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1102">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1103">
                    <AlertCircleIcon className="w-5 h-5 text-viridia" data-id="element-1104" />
                  </div>
                  <div data-id="element-1105">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1106">
                      Complex Approval Processes
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1107">
                      Quotes over a certain amount need manager approval, or
                      specific job types need to be routed to specific
                      technicians based on logic.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1108">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1109">
                    <DatabaseIcon className="w-5 h-5 text-viridia" data-id="element-1110" />
                  </div>
                  <div data-id="element-1111">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1112">
                      Heavy Admin Burden
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1113">
                      Your team spends hours every week copy-pasting information
                      from emails into spreadsheets or generating manual
                      reports.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1114">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-1115">
                <h3 className="text-xl font-bold text-white mb-6 text-center" data-id="element-1116">
                  The Cost of Manual Work
                </h3>
                <div className="space-y-6" data-id="element-1117">
                  <div data-id="element-1118">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1119">
                      <span className="text-gray-400" data-id="element-1120">Manual Data Entry</span>
                      <span className="text-red-400" data-id="element-1121">High Error Rate</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1122">
                      <div className="bg-red-500 h-2 rounded-full" style={{
                      width: '85%'
                    }} data-id="element-1123"></div>
                    </div>
                  </div>
                  <div data-id="element-1124">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1125">
                      <span className="text-gray-400" data-id="element-1126">Information Silos</span>
                      <span className="text-yellow-400" data-id="element-1127">Slow Handoffs</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1128">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{
                      width: '70%'
                    }} data-id="element-1129"></div>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-800" data-id="element-1130">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1131">
                      <span className="text-white font-medium" data-id="element-1132">
                        Automated System
                      </span>
                      <span className="text-viridia font-medium" data-id="element-1133">
                        Instant & Accurate
                      </span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1134">
                      <div className="bg-viridia h-2 rounded-full" style={{
                      width: '100%'
                    }} data-id="element-1135"></div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section className="py-20 bg-viridia-surface" data-id="element-1136">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1137">
          <ScrollReveal data-id="element-1138">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1139">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1140">
                Examples of Custom Workflows
              </h2>
              <p className="text-gray-400" data-id="element-1141">
                If it has an API, we can connect it. Here are a few examples of
                systems we can build.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1142">
            {[{
            title: 'Intelligent Lead Routing',
            desc: 'Web form submissions are analyzed by AI. High-value commercial leads are texted directly to the owner, while residential leads go to the standard sales queue.',
            icon: GitMergeIcon
          }, {
            title: 'Cross-Platform Syncing',
            desc: 'When a job is marked "Complete" in Jobber, automatically generate an invoice in QuickBooks and send a review request via Viridia CRM.',
            icon: WorkflowIcon
          }, {
            title: 'AI Call Summaries to Slack',
            desc: "After every recorded sales call, AI generates a 3-bullet summary of the customer's needs and posts it to a Slack channel for the fulfillment team.",
            icon: MessageSquareIcon
          }, {
            title: 'Automated Reporting',
            desc: 'Pull data from Google Ads, Facebook Ads, and your CRM into a custom Google Sheet that updates daily without manual export/import.',
            icon: FileTextIcon
          }, {
            title: 'Inventory Alerts',
            desc: 'When a specific service is booked, automatically check inventory levels in your management software and alert the purchasing manager if parts are needed.',
            icon: AlertCircleIcon
          }, {
            title: 'Custom Webhooks',
            desc: 'Need your proprietary internal software to trigger actions in modern marketing tools? We build the middleware to make it happen.',
            icon: SettingsIcon
          }].map((example, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-1143">
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 h-full hover:border-viridia/30 transition-colors" data-id="element-1144">
                  <div className="w-10 h-10 rounded bg-viridia-surface border border-gray-700 flex items-center justify-center mb-4" data-id="element-1145">
                    <example.icon className="w-5 h-5 text-viridia" data-id="element-1146" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-1147">
                    {example.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-1148">
                    {example.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-viridia-black" data-id="element-1149">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1150">
          <ScrollReveal data-id="element-1151">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1152">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1153">
                Our Engineering Process
              </h2>
              <p className="text-gray-400" data-id="element-1154">
                Custom builds require careful planning. We don't write a line of
                code until we understand your business logic.
              </p>
            </div>
          </ScrollReveal>

          <div className="relative" data-id="element-1155">
            {/* Connecting line */}
            <div className="hidden lg:block absolute top-12 left-0 w-full h-0.5 bg-gray-800 z-0" data-id="element-1156"></div>

            <div className="grid lg:grid-cols-5 gap-8" data-id="element-1157">
              {[{
              step: '1',
              title: 'Discover',
              desc: 'We map out your current manual process and identify bottlenecks.',
              icon: SearchIcon
            }, {
              step: '2',
              title: 'Architect',
              desc: 'We design the logic flow and select the right APIs and tools.',
              icon: MapIcon
            }, {
              step: '3',
              title: 'Build',
              desc: 'We engineer the connections, webhooks, and data transformations.',
              icon: WrenchIcon
            }, {
              step: '4',
              title: 'Test',
              desc: 'Rigorous testing with edge cases to ensure data integrity.',
              icon: ActivityIcon
            }, {
              step: '5',
              title: 'Launch',
              desc: 'Deployment, team training, and ongoing monitoring.',
              icon: RocketIcon
            }].map((phase, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-1158">
                  <div className="relative z-10 flex flex-col items-center text-center" data-id="element-1159">
                    <div className="w-24 h-24 rounded-full bg-viridia-surface border-4 border-viridia-black flex items-center justify-center mb-4 shadow-[0_0_0_2px_rgba(31,41,55,1)]" data-id="element-1160">
                      <phase.icon className="w-8 h-8 text-viridia" data-id="element-1161" />
                    </div>
                    <div className="text-xs font-bold text-viridia mb-1" data-id="element-1162">
                      PHASE {phase.step}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1163">
                      {phase.title}
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-1164">{phase.desc}</p>
                  </div>
                </ScrollReveal>)}
            </div>
          </div>
        </div>
      </section>

      {/* Scope Clarity Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-1165">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1166">
          <div className="bg-viridia-black border border-gray-800 rounded-3xl overflow-hidden" data-id="element-1167">
            <div className="grid md:grid-cols-2" data-id="element-1168">
              <div className="p-10 border-b md:border-b-0 md:border-r border-gray-800" data-id="element-1169">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-1170">
                  Standard Packages
                </h3>
                <p className="text-gray-400 text-sm mb-6" data-id="element-1171">
                  Our standard AI Lead Conversion and CRM setups are pre-built
                  systems designed for rapid deployment.
                </p>
                <ul className="space-y-3" data-id="element-1172">
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1173">
                    <CheckCircleIcon className="w-4 h-4 text-gray-500" data-id="element-1174" /> Fixed
                    monthly pricing
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1175">
                    <CheckCircleIcon className="w-4 h-4 text-gray-500" data-id="element-1176" />{' '}
                    Deployed in 7 days
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1177">
                    <CheckCircleIcon className="w-4 h-4 text-gray-500" data-id="element-1178" />{' '}
                    Proven, templated workflows
                  </li>
                </ul>
              </div>
              <div className="p-10 bg-gradient-to-br from-viridia/5 to-transparent" data-id="element-1179">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-1180">
                  Custom Automations
                </h3>
                <p className="text-gray-400 text-sm mb-6" data-id="element-1181">
                  Bespoke engineering work to connect third-party tools or build
                  entirely unique logic for your business.
                </p>
                <ul className="space-y-3" data-id="element-1182">
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1183">
                    <CheckCircleIcon className="w-4 h-4 text-viridia" data-id="element-1184" /> Scoped
                    and quoted per project
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1185">
                    <CheckCircleIcon className="w-4 h-4 text-viridia" data-id="element-1186" />{' '}
                    Timeline based on complexity
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1187">
                    <CheckCircleIcon className="w-4 h-4 text-viridia" data-id="element-1188" /> 100%
                    tailored to your operations
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-1189">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1190">
          <ScrollReveal data-id="element-1191">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1192">
              Let's Map Out Your System
            </h2>
            <p className="text-xl text-gray-400 mb-10" data-id="element-1193">
              Book a technical scoping call. We'll review your current software
              stack and identify exactly where automation can save you time and
              money.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1194">
              Book a Scoping Call
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}